import type { DataProvider } from 'react-admin';
import type { Supplier, SupplierBankAccount, VatInvoice } from '../types';
import { resolveBankName } from './bankResolver';
import { extractMeInvoiceDetail, fetchMeInvoiceDetail } from './meInvoiceClient';

const digits = (value?: string) => String(value || '').replace(/\D/g, '');
const normalized = (value?: string) => String(value || '').trim().toLocaleLowerCase('vi-VN').replace(/\s+/g, ' ');

function completeAccount(supplier: Supplier): SupplierBankAccount | null {
  const accounts = supplier.bankAccounts || [];
  const selected = accounts.find((account) => account.isPrimary) || (accounts.length === 1 ? accounts[0] : undefined);
  if (selected) return selected.accountNumber && selected.bankBin ? selected : null;
  if (supplier.accountNumber && supplier.bankBin) {
    return { accountNumber: supplier.accountNumber, bankBin: supplier.bankBin, bankName: supplier.bankName };
  }
  return null;
}

function assertMatchingDetail(invoice: VatInvoice, detail: ReturnType<typeof extractMeInvoiceDetail>) {
  if (detail.meInvoiceId && invoice.meInvoiceId && detail.meInvoiceId !== invoice.meInvoiceId) {
    throw new Error('Chi tiết meInvoice không khớp định danh hóa đơn đã chọn.');
  }
  if (detail.invoiceNumber && normalized(detail.invoiceNumber) !== normalized(invoice.invoiceNumber)) {
    throw new Error('Chi tiết meInvoice không khớp số hóa đơn đã chọn.');
  }
  if (detail.taxCode && invoice.taxCode && digits(detail.taxCode) !== digits(invoice.taxCode)) {
    throw new Error('Chi tiết meInvoice không khớp mã số thuế nhà cung cấp.');
  }
  if (detail.invoiceDate && invoice.invoiceDate && detail.invoiceDate !== invoice.invoiceDate) {
    throw new Error('Chi tiết meInvoice không khớp ngày hóa đơn đã chọn.');
  }
  if (detail.amount > 0 && invoice.amount > 0 && Math.abs(detail.amount - invoice.amount) > 1) {
    throw new Error('Chi tiết meInvoice không khớp tổng tiền hóa đơn đã chọn.');
  }
}

export async function enrichSupplierBankFromMeInvoice(
  dp: DataProvider,
  invoice: VatInvoice,
  supplier: Supplier,
): Promise<Supplier> {
  if (completeAccount(supplier)) return supplier;

  let bankAccount = invoice.meInvoiceSellerBankAccount || '';
  let sourceBankName = invoice.meInvoiceSellerBankName || '';
  if (!bankAccount || !sourceBankName) {
    if (!invoice.meInvoiceId) throw new Error('Hóa đơn chưa có định danh meInvoice để lấy thông tin ngân hàng.');
    const detail = extractMeInvoiceDetail(await fetchMeInvoiceDetail(invoice.meInvoiceId));
    assertMatchingDetail(invoice, detail);
    bankAccount = detail.bankAccount;
    sourceBankName = detail.bankName;
    await dp.update<VatInvoice>('vatInvoices', {
      id: invoice.id,
      data: {
        ...invoice,
        meInvoiceDetailFetchedAt: new Date().toISOString(),
        meInvoiceSellerBankAccount: bankAccount,
        meInvoiceSellerBankName: sourceBankName,
      },
      previousData: invoice,
    });
  }

  if (!bankAccount) throw new Error('Chi tiết hóa đơn meInvoice không có số tài khoản người bán.');
  if (!sourceBankName) throw new Error('Chi tiết hóa đơn meInvoice không có tên ngân hàng người bán.');
  const resolved = await resolveBankName(sourceBankName);
  if (!resolved) throw new Error(`Không xác định được ngân hàng từ thông tin meInvoice: ${sourceBankName}`);

  const accounts = [...(supplier.bankAccounts || [])];
  const currentPrimary = accounts.find((account) => account.isPrimary);
  if (currentPrimary && !currentPrimary.accountNumber) {
    throw new Error('Tài khoản ngân hàng chính đã lưu chưa có số tài khoản; cần kiểm tra thủ công.');
  }
  const knownNumbers = [supplier.accountNumber, ...accounts.map((account) => account.accountNumber)].filter(Boolean);
  if (knownNumbers.length && !knownNumbers.some((number) => digits(number) === digits(bankAccount))) {
    throw new Error('Tài khoản ngân hàng trên meInvoice khác dữ liệu đã lưu; cần kiểm tra thủ công.');
  }

  const existingIndex = accounts.findIndex((account) => digits(account.accountNumber) === digits(bankAccount));
  const hasPrimary = accounts.some((account) => account.isPrimary);
  const enrichedAccount: SupplierBankAccount = {
    ...(existingIndex >= 0 ? accounts[existingIndex] : {}),
    accountNumber: bankAccount,
    bankName: existingIndex >= 0 && accounts[existingIndex].bankName ? accounts[existingIndex].bankName : resolved.bankName,
    bankBin: existingIndex >= 0 && accounts[existingIndex].bankBin ? accounts[existingIndex].bankBin : resolved.bankBin,
    accountHolder: existingIndex >= 0 && accounts[existingIndex].accountHolder ? accounts[existingIndex].accountHolder : supplier.name,
    isPrimary: existingIndex >= 0 ? accounts[existingIndex].isPrimary || !hasPrimary : !hasPrimary,
    mappingSource: existingIndex >= 0 && accounts[existingIndex].mappingSource ? accounts[existingIndex].mappingSource : resolved.mappingSource,
    mappingConfidence: existingIndex >= 0 && accounts[existingIndex].mappingConfidence != null
      ? accounts[existingIndex].mappingConfidence
      : resolved.confidence,
  };
  if (existingIndex >= 0) accounts[existingIndex] = enrichedAccount;
  else accounts.push(enrichedAccount);

  const updated: Supplier = {
    ...supplier,
    bankAccounts: accounts,
    accountNumber: supplier.accountNumber || enrichedAccount.accountNumber,
    bankName: supplier.bankName || enrichedAccount.bankName,
    bankBin: supplier.bankBin || enrichedAccount.bankBin,
    accountHolder: supplier.accountHolder || enrichedAccount.accountHolder,
  };
  return (await dp.update<Supplier>('suppliers', {
    id: supplier.id,
    data: updated,
    previousData: supplier,
  })).data;
}
